/**
 * Centralized inline-SVG icon set (docs/AGENT_STATUS.md "OWNER HANDS-ON TEST punch-list ROUND 2"
 * #10b — "generate icons for ALL buttons"). One glyph per semantic ACTION (not per button), so the
 * same action renders the same icon everywhere it appears (Save always looks like Save, Delete
 * always looks like a trash can, etc.) — a small, consistent, hand-authored line-icon set (24x24
 * viewBox, `stroke="currentColor"`, no external asset/font/network dependency, so it can never
 * 404 and is themeable purely via CSS `color`). Labels stay in the markup alongside the icon
 * (icons augment, not replace, the existing text — CLAUDE.md/V2_EDITOR_UX §9 asked for icons,
 * not an icon-only toolbar).
 *
 * Two call shapes, matching how each caller builds its DOM:
 *  - `icon(name)` returns a raw `<svg>...</svg>` string for template-literal-built markup
 *    (editor-page.ts, login-page.ts, riders-list-page.ts).
 *  - `iconEl(name)` returns a live `SVGSVGElement` for `document.createElement`-style builders
 *    (list-tables.ts, object-params-panel.ts, preset-menu.ts, dom-utils.ts).
 */

export type IconName =
  | "new"
  | "save"
  | "save-as"
  | "copy"
  | "print"
  | "export"
  | "properties"
  | "rotate-left"
  | "rotate-right"
  | "upload"
  | "attach"
  // SCRUM-552: the Input/Output List's "Unlink" — the `attach` chain with a stroke through it.
  | "unlink"
  | "scope-group"
  | "scope-item"
  | "search"
  | "delete-tree"
  | "publish"
  | "exit"
  | "grid"
  // The view switcher's own three icons (SCRUM-410). Owner mockup — "Один переключатель вида",
  // ticket brief §5 — first shipped `topView`/`planView` as placeholders ("the owner has not picked
  // new ones yet"); the owner picked the real set the same day: «давай Л, только третью иконку
  // надо сделать просто пустым квадратиком. Как вторую, но незакрашенным». `topView`/`planView`
  // themselves were REMOVED outright (not merely left unused) once grepped clean of every other
  // caller — new names below, not a redraw in place, same reasoning `captions`/`gridLattice` below
  // already used for their own predecessors.
  | "view3d"
  // "Top view": a square with a FILLED inner square.
  | "viewTop"
  // "Plan sheet": the SAME square, inner square left UNFILLED — the owner's "L, но третью иконку
  // пустым квадратиком, как вторую, но незакрашенным".
  | "viewSheet"
  | "labels"
  // The captions/annotations toggle's own glyph (SCRUM-403 v2, owner ask 27.09.2026 — "вариант Б
  // облачко"): a speech bubble with two text lines, replacing the price-tag `labels` icon on
  // `#btn-show-anchors` ONLY. `labels` itself is UNCHANGED and stays in use elsewhere (the preset
  // tag chips in `preset-menu.ts` — a real price tag, correct there) — this is a new name, not a
  // redraw in place, per CLAUDE.md rule 36 ("не переделывай существующую иконку, вдруг она
  // используется где-то ещё").
  | "captions"
  // `#btn-show-grid`'s own glyph (SCRUM-403 v2, owner ask 27.09.2026 — "решётка", "вариант ж"): an
  // open lattice (three lines each way, no frame), replacing the four-squares `grid` icon on THAT
  // button only. `grid` itself is UNCHANGED — grepped, it had no other caller at the time of this
  // change — new name, not a redraw in place, same rule 36 reasoning as `captions` above.
  | "gridLattice"
  | "riders-list"
  | "logout"
  | "login"
  | "add"
  | "delete"
  | "select"
  | "add-child"
  | "rotate"
  | "rotate-tree"
  | "close"
  | "check"
  | "edit"
  | "chevron-down"
  // The mirror of `chevron-down`, for a sort header reading "ascending" (SCRUM-570, admin users
  // table). A new name, not a rotation in CSS, so the pair renders identically everywhere.
  | "chevron-up"
  | "chevron-left"
  | "chevron-right"
  | "stage"
  | "input-list"
  | "output-list"
  | "gear-list"
  | "help"
  | "group-sections"
  | "group-kits"
  | "collapse-all"
  | "expand-all"
  | "presets"
  | "save-preset"
  | "reorder"
  | "undo"
  | "redo"
  | "menu"
  // AI assistant (docs/V2_AI_ASSISTANT.md) — a sparkle, the conventional "assisted/generated" affordance.
  | "assistant"
  // Voice input for the assistant (§7) — a microphone.
  | "mic"
  // The riders-list page's future service-menu sidebar (owner ask 2026-07-22 #6 — v1's "dream
  // menu": Home/Soundman/My Riders/Inbox/Outbox/Event manager/Warehouse/Show Calendar/My Profile).
  // Only "My Riders" (`riders-list`, already existed) and "My Profile" (`properties`, already
  // existed) are WIRED; the rest render disabled placeholders, but every button on the page still
  // needs an icon (chrome-layout.spec.ts's page-wide icon sweep), hence these new glyphs.
  | "home"
  | "soundman"
  | "inbox"
  | "outbox"
  | "event-manager"
  | "warehouse"
  | "calendar"
  // docs/V2_IO_KEEP_ON_DELETE.md — the delete-time "Optimize I/O" / "Keep as is" dialog buttons.
  | "io-optimize"
  | "io-keep"
  // Замочек листа плана (владелец, 27.10.2026: «какой то замочек рядом с ним»): закрытый — лист
  // закреплён и рукой не двигается, открытый — двигается.
  | "lock"
  | "lock-open"
  | "patch"
  // SCRUM-411 — the docked right panel's own tab/rail glyphs, taken VERBATIM from the owner-approved
  // prototype (`docs/prototypes/2026-09-27-editor-layout.html`, symbols `letterI`/`letterO`/`gear`/
  // `rider`): a stylized "I" and "O" for the Input/Output List tabs (replacing the old `input-list`/
  // `output-list` arrow glyphs + text-badge letters in that one slot), a mascot flight-case on wheels
  // for the Gear List tab (replacing the cog `gear-list` there — `gear-list` itself is UNCHANGED and
  // untouched, this is a new name per rule 36, not a redraw), and a simple page/document for the
  // Rider tab (replacing `stage`, also left untouched elsewhere). `letterO` is drawn in a 30×24
  // (wide) viewBox in the prototype — see `VIEWBOXES` below, which is what makes it render 20px wide
  // at a 16px-tall icon rather than being squeezed into a square box like every other glyph.
  // ЭКСПОРТ В PDF — БУКВЫ, А НЕ ПРИНТЕР (владелец, 13.10.2026: «давай заменим везде эту идиотскую
  // кнопку печати… на PDF»). Широкая клетка, как у `letterO`: три буквы в квадрате 24×24 были бы
  // нечитаемы. Запрета на слово «PDF» нет — это название формата; нельзя чужой ЛОГОТИП.
  | "pdf"
  // ОТПРАВИТЬ ССЫЛКУ (владелец, 14.10.2026: «надо отправить вообще. Там и почта и воцап и
  // телеграм и всякие мессенджеры»). Три узла и две связи — общая метка «поделиться», та же, что
  // у системного окна; не логотип ни одного мессенджера.
  | "share"
  | "letterI"
  | "letterO"
  | "gear"
  | "rider"
  // The side-nav's "Offers" item (PR3, `docs/V2_OFFER_TECH_APPROVAL.md`) — the same document shape
  // `rider` uses, plus a checkmark: a rider that has been answered.
  | "offers"
  // The docked panel's own collapse/expand toggle (SCRUM-411 prototype symbol `collapse`) — a panel
  // outline with a vertical divider line, read as "fold this pane away".
  | "collapse"
  // The topbar's own Priority+ overflow button (SCRUM-411 §1) — the prototype renders "⋯" as plain
  // text (`.more`, no `<symbol>`), but every OTHER button in this bar carries an `icons.ts` glyph
  // (chrome-layout.spec.ts's page-wide icon sweep), so this is a new, minimal three-dot glyph rather
  // than the one text-only button in the app.
  | "overflow"
  // SCRUM-457: the riser's "Resize on stage" toggle in Properties — a floor block with an arrow
  // pulling its corner out, the same move the on-stage handles make.
  | "resize"
  // SCRUM-779: a position with no serial number yet — the way into its list (a barcode: what a serial number is read from).
  | "barcode";

// Every path below is hand-authored geometry (rects/circles/lines/simple paths) — not sourced
// from any third-party icon font/library, so there's no licensing question to track.
const PATHS: Record<IconName, string> = {
  new: '<path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/><path d="M14 2v6h6"/><path d="M12 12v6M9 15h6"/>',
  save: '<path d="M4 4h13l3 3v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/><path d="M8 4v6h8V4"/><rect x="7" y="14" width="10" height="6"/>',
  // Save + a pen: "save under a different name", the same metaphor as the Edit icon's nib.
  "save-as":
    '<path d="M4 4h9l3 3v6"/><path d="M4 4v15a1 1 0 0 0 1 1h6"/><path d="M8 4v5h6V4"/><path d="M14 20h3l4.5-4.5a1.8 1.8 0 0 0-2.5-2.5L14.5 17.5V20z"/>',
  // Two offset sheets — the standard "duplicate" mark, deliberately NOT the presets stack (which is
  // two SQUARES and already means "the preset library").
  copy: '<rect x="9" y="3" width="12" height="14" rx="1.5"/><path d="M15 21H4.5A1.5 1.5 0 0 1 3 19.5V7"/>',
  // Буквы рисуются заливкой, а не обводкой: у этого набора обводка 2 px, и ею буквы заплыли бы.
  pdf: '<text x="18" y="18" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-size="17" font-weight="700" fill="currentColor" stroke="none">PDF</text>',
  share:
    '<circle cx="18" cy="5" r="2.6"/><circle cx="6" cy="12" r="2.6"/><circle cx="18" cy="19" r="2.6"/><path d="M8.3 10.8 15.7 6.4"/><path d="M8.3 13.2 15.7 17.6"/>',
  print:
    '<rect x="6" y="9" width="12" height="7" rx="1"/><path d="M6 9V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5"/><path d="M8 16v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-4"/>',
  export: '<path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M4 19h16"/>',
  // v1 `properties_button` ("Properties of object") — a sliders/settings panel, deliberately
  // distinct from `edit` (a pencil, "edit this row's value") so the two never read as the same action.
  properties: '<path d="M5 6h14M5 12h14M5 18h14"/><circle cx="9" cy="6" r="2"/><circle cx="15" cy="12" r="2"/><circle cx="8" cy="18" r="2"/>',
  // v1 `rot-ccw`/`rot-cw` ("Rotate left"/"Rotate right") — directional variants of the generic
  // `rotate` glyph (which stays as-is for the object-params panel's own arbitrary-angle rotate).
  "rotate-left": '<path d="M20 12a8 8 0 1 0-2.6 5.9"/><path d="M20 17v-4h-4"/>',
  "rotate-right": '<path d="M4 12a8 8 0 1 1 2.6 5.9"/><path d="M4 17v-4h4"/>',
  // v1 `addobject_button` ("Upload 3d object") — an arrow rising OUT of a tray, the inverse of
  // `export`'s arrow falling into a baseline.
  upload: '<path d="M12 20V8"/><path d="M7 13l5-5 5 5"/><path d="M4 4h16"/>',
  // v1 `attach` ("Attach object to another") — a link/chain, the standard "bind these two" glyph.
  attach:
    '<path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1.5 1.5"/><path d="M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1.5-1.5"/>',
  unlink:
    '<path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1.5 1.5"/><path d="M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1.5-1.5"/><path d="M4 4l16 16"/>',
  // РЕЖИМ ВЫДЕЛЕНИЯ — пара значков, а не один подсвеченный (SCRUM-401, владелец 27.09.2026):
  // «вся стопка» — два листа друг за другом, «один предмет» — один лист. Выбрано владельцем из листа
  // вариантов 28.09.2026 (вариант №1 из пяти, `Документы/SoundCheckBOX/иконки-переключатель-2026-09-28.pdf`)
  // на замену первой рабочей паре — пунктирной рамке с квадратиками, про которую он сказал коротко и
  // ясно, что она никуда не годится.
  //
  // Почему именно стопка листов: этот образ означает «сгруппировано» в каждом редакторе, который
  // человек уже держал в руках, и отличается от одиночного листа СИЛУЭТОМ, а не деталью внутри. В
  // панели значок рисуется 18 пикселями, и на таком размере читается только силуэт.
  "scope-group":
    '<rect x="3" y="8.5" width="13" height="10" rx="1.5"/><path d="M6.5 5.5h13a1.5 1.5 0 0 1 1.5 1.5v9"/><path d="M5 7h13a1.5 1.5 0 0 1 1.5 1.5v9" opacity="0.45"/>',
  "scope-item": '<rect x="5" y="6" width="14" height="13" rx="1.5"/>',
  // Лупа — поиск по каталогу пресетов (шаг 2.1). Кружок и ручка, ничего больше: на 14 пикселях в
  // панели любая деталь внутри линзы превращается в грязь.
  search: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21"/>',
  // v1 `deletechild` ("Delete object with all children") — the `delete` trash can plus the
  // descendant nodes of `rotate-tree`, so it reads as "delete, including the subtree".
  "delete-tree":
    '<path d="M3 6h11"/><path d="M6.5 6V4h4v2"/><path d="M4.5 6l.8 12a1 1 0 0 0 1 1h5.4a1 1 0 0 0 1-1l.8-12"/><circle cx="19" cy="7" r="1.6"/><circle cx="19" cy="13" r="1.6"/><circle cx="19" cy="19" r="1.6"/><path d="M16 7h-1.5M16 13h-1.5M16 19h-1.5"/>',
  // v1 `stage_button` ("Publish rider") — an upload-to-cloud/share glyph: make it publicly visible.
  publish: '<path d="M12 16V4"/><path d="M8 8l4-4 4 4"/><path d="M5 14v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5"/>',
  // v1 `exit_button` ("Exit to My Riders") — a door with an arrow leaving it. Deliberately mirrors
  // `logout`'s geometry (same "leave through a door" family) but points the other way, so "exit to
  // the riders list" never reads as "log out".
  exit: '<path d="M14 20h5a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-5"/><path d="M9 8l-4 4 4 4"/><path d="M5 12h9"/>',
  // View switcher "3D" position — an isometric cube, SCRUM-410 (owner-specified path, ticket brief §5).
  view3d: '<path d="M12 3 20 7.5v9L12 21l-8-4.5v-9z"/><path d="M4 7.5 12 12l8-4.5M12 12v9"/>',
  // "Top view" — a square with a FILLED inner square (owner-specified path, SCRUM-410).
  viewTop: '<rect x="4" y="4" width="16" height="16" rx="1.5"/><rect x="8" y="8" width="8" height="8" fill="currentColor"/>',
  // "Plan sheet" — the SAME square, inner square NOT filled (owner-specified path, SCRUM-410).
  viewSheet: '<rect x="4" y="4" width="16" height="16" rx="1.5"/><rect x="8" y="8" width="8" height="8"/>',
  grid: '<rect x="3" y="3" width="6" height="6"/><rect x="15" y="3" width="6" height="6"/><rect x="3" y="15" width="6" height="6"/><rect x="15" y="15" width="6" height="6"/>',
  labels:
    '<path d="M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z"/><circle cx="8" cy="8" r="1.5"/>',
  // `#btn-show-anchors` — the speech-bubble-with-two-lines glyph the owner picked ("variant Б").
  captions: '<path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-9l-4 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/><path d="M7 8.5h10M7 12h6"/>',
  // `#btn-show-grid` — the open lattice the owner picked ("variant ж").
  gridLattice: '<path d="M6 3v18M12 3v18M18 3v18M3 6h18M3 12h18M3 18h18"/>',
  "riders-list": '<path d="M4 4h16v4H4z"/><path d="M4 10h16v10H4z"/><path d="M8 14h8M8 17h5"/>',
  logout: '<path d="M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4"/><path d="M15 16l5-4-5-4"/><path d="M20 12H9"/>',
  login: '<path d="M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4"/><path d="M9 8l-4 4 4 4"/><path d="M5 12h11"/>',
  add: '<path d="M12 5v14M5 12h14"/>',
  delete: '<path d="M4 7h16"/><path d="M9 7V4h6v3"/><path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13"/><path d="M10 11v6M14 11v6"/>',
  select: '<path d="M5 3l6.5 16 2-6.5L20 10.5z"/>',
  "add-child": '<path d="M6 3v8a3 3 0 0 0 3 3h6"/><circle cx="18" cy="18" r="3"/><path d="M18 16.5v3M16.5 18h3"/>',
  rotate: '<path d="M4 12a8 8 0 1 1 2.6 5.9"/><path d="M4 17v-4h4"/>',
  "rotate-tree": '<path d="M4 12a8 8 0 1 1 2.6 5.9"/><path d="M4 17v-4h4"/><circle cx="18" cy="6" r="1.6"/><circle cx="20" cy="12" r="1.6"/>',
  close: '<path d="M5 5l14 14M19 5L5 19"/>',
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  edit: '<path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z"/><path d="M13.5 7.5l3 3"/>',
  "chevron-down": '<path d="M5 9l7 7 7-7"/>',
  "chevron-up": '<path d="M5 15l7-7 7 7"/>',
  "chevron-left": '<path d="M15 5l-7 7 7 7"/>',
  "chevron-right": '<path d="M9 5l7 7-7 7"/>',
  stage: '<rect x="3" y="14" width="18" height="4" rx="1"/><path d="M7 14V9a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v5"/>',
  "input-list": '<path d="M3 12h11"/><path d="M10 8l4 4-4 4"/><line x1="19" y1="5" x2="19" y2="19"/>',
  "output-list": '<line x1="5" y1="5" x2="5" y2="19"/><path d="M8 12h11"/><path d="M15 8l4 4-4 4"/>',
  "gear-list":
    '<circle cx="12" cy="12" r="3"/><path d="M12 2.5v3M12 18.5v3M4.6 4.6l2.2 2.2M17.2 17.2l2.2 2.2M2.5 12h3M18.5 12h3M4.6 19.4l2.2-2.2M17.2 6.8l2.2-2.2"/>',
  presets: '<rect x="3" y="3" width="12" height="12" rx="1"/><rect x="9" y="9" width="12" height="12" rx="1"/>',
  // "presets" (the stacked tiles) + a down-arrow INTO them: save this object into the preset library.
  "save-preset": '<rect x="3" y="3" width="11" height="11" rx="1"/><rect x="10" y="10" width="11" height="11" rx="1"/><path d="M15.5 13v5M13 15.5l2.5 2.5 2.5-2.5"/>',
  reorder: '<path d="M8 9l4-4 4 4"/><path d="M8 15l4 4 4-4"/>',
  // Undo/redo — a curved arrow doubling back on itself. `redo` is the mirror, so the pair reads as
  // opposite directions at a glance (left = back, right = forward).
  undo: '<path d="M9 7L4 12l5 5"/><path d="M4 12h11a5 5 0 0 1 0 10h-1"/>',
  redo: '<path d="M15 7l5 5-5 5"/><path d="M20 12H9a5 5 0 0 0 0 10h1"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  // A large four-point sparkle plus a small one — the common "AI / magic assist" glyph.
  assistant:
    '<path d="M12 3l1.7 4.8L18.5 9.5l-4.8 1.7L12 16l-1.7-4.8L5.5 9.5l4.8-1.7z"/><path d="M18 14l.6 1.7 1.7.6-1.7.6L18 18.6l-.6-1.7-1.7-.6 1.7-.6z"/>',
  // Microphone — the assistant's voice input (push-to-talk).
  mic: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v4M9 21h6"/>',
  // --- riders-list side-nav (owner ask 2026-07-22 #6) — see the IconName union above for context.
  home: '<path d="M4 11l8-7 8 7"/><path d="M6 10v9a1 1 0 0 0 1 1h3v-6h4v6h3a1 1 0 0 0 1-1v-9"/>',
  // Headphones — the "Soundman" role area (a future per-role dashboard, not the audio-list "Input").
  soundman: '<path d="M4 13a8 8 0 0 1 16 0"/><rect x="3" y="13" width="4" height="6" rx="1.2"/><rect x="17" y="13" width="4" height="6" rx="1.2"/>',
  // Tray with an arrow pointing INTO it — mail arriving.
  inbox: '<path d="M4 12h4l2 3h4l2-3h4"/><path d="M4 12V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v6"/><path d="M4 12v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6"/>',
  // Same tray, but the arrow rises OUT of it first — mail leaving (mirrors `inbox`'s silhouette so
  // the pair reads as a matched set, the same way `undo`/`redo` do).
  outbox: '<path d="M12 13V5"/><path d="M9 8l3-3 3 3"/><path d="M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6"/>',
  // Clipboard with a checkmark — managing/approving events, distinct from the plain grid of `calendar`.
  "event-manager":
    '<rect x="5" y="4" width="14" height="17" rx="1.5"/><path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1"/><path d="M9 12.5l2 2 4-4.5"/>',
  // A building with a bay door — storage/rental stock.
  warehouse: '<path d="M3 21h18"/><path d="M4 21V9l8-5 8 5v12"/><path d="M9 21v-6h6v6"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18"/><path d="M8 3v4M16 3v4"/><circle cx="8" cy="14" r="1.1"/><circle cx="12" cy="14" r="1.1"/><circle cx="16" cy="14" r="1.1"/>',
  // "Optimize I/O" — two arrows pointing INWARD along a center line: close the gap left by the
  // deleted channel(s), the opposite silhouette of `export`'s single outward arrow.
  "io-optimize": '<path d="M3 12h6"/><path d="M9 12l-3.5-3.5"/><path d="M9 12l-3.5 3.5"/><path d="M21 12h-6"/><path d="M15 12l3.5-3.5"/><path d="M15 12l3.5 3.5"/>',
  // "Keep as is" — a map pin: the channel's number stays planted right where it is.
  "io-keep": '<path d="M12 2.5a5.5 5.5 0 0 0-5.5 5.5c0 4 5.5 12 5.5 12s5.5-8 5.5-12A5.5 5.5 0 0 0 12 2.5z"/><circle cx="12" cy="8" r="2"/>',
  // Замок листа. РАЗНИЦА ЧИТАЕТСЯ С ОДНОГО ВЗГЛЯДА (владелец, 27.10.2026: «ненажатый замочек должен
  // рисоваться раскрытым нажатый закрытым иначе когнитивный диссонанс, сейчас только выделяется
  // цветом кнопка»). Первая пара отличалась длиной дуги на полтора пикселя при размере 18 — на
  // экране это и правда не разница. Теперь у закрытого скоба стоит НАД телом и опущена обоими
  // концами, у открытого — сдвинута к правому краю и поднята, то есть силуэт разный целиком.
  lock: '<rect x="5" y="10.5" width="14" height="10.5" rx="1.6"/><path d="M8.5 10.5V7.2a3.5 3.5 0 0 1 7 0v3.3"/>',
  "lock-open": '<rect x="5" y="10.5" width="14" height="10.5" rx="1.6"/><path d="M15 10.5V6.8a3.4 3.4 0 0 1 6.8 0"/>',
  // Patch Editor / Stage Commutation (XLR-3 bold circular connector with 3 pins)
  patch: '<circle cx="12" cy="12" r="9.2" stroke-width="2.4"/><circle cx="8" cy="9.5" r="2.2" fill="currentColor"/><circle cx="16" cy="9.5" r="2.2" fill="currentColor"/><circle cx="12" cy="15.5" r="2.2" fill="currentColor"/>',
  // Help / Tour / Question mark in a circle
  // The gear list's toolbar (SCRUM-361): group by section (stacked rows), by kit (boxed clusters),
  // fold everything (chevrons pointing in), open everything (chevrons pointing out).
  "group-sections": '<path d="M4 6h16M4 12h16M4 18h16"/><circle cx="4" cy="6" r="0.6"/>',
  "group-kits": '<rect x="3" y="4" width="8" height="7" rx="1"/><rect x="13" y="4" width="8" height="7" rx="1"/><rect x="3" y="13" width="18" height="7" rx="1"/>',
  "collapse-all": '<path d="M7 4l5 5 5-5"/><path d="M7 20l5-5 5 5"/>',
  "expand-all": '<path d="M7 9l5-5 5 5"/><path d="M7 15l5 5 5-5"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  // SCRUM-411 — taken verbatim from the prototype's own `<symbol>` paths (see the IconName union
  // above for provenance/why each one is a new glyph rather than a redraw).
  letterI: '<circle cx="5.5" cy="3.6" r="1.6" fill="currentColor" stroke="none"/><path d="M5.5 8.5V21M11 8.5V21M11 13a4.2 4.2 0 0 1 8.4 0V21"/>',
  letterO: '<ellipse cx="5.2" cy="14.75" rx="4" ry="6.25"/><path d="M12 8.5V16.3a3.3 3.3 0 0 0 6.6 0M18.6 8.5V21M23.6 4V17.8a2.9 2.9 0 0 0 4.2 2.7M20.8 8.5h6.4"/>',
  gear: '<rect x="3" y="4" width="18" height="11.5" rx="1"/><ellipse cx="7" cy="19" rx="2.2" ry="1.9"/><ellipse cx="17" cy="19" rx="2.2" ry="1.9"/>',
  rider: '<path d="M6 3h9l4 4v14H6z"/><path d="M15 3v4h4M9 12h7M9 16h7"/>',
  offers: '<path d="M6 3h9l4 4v14H6z"/><path d="M15 3v4h4"/><path d="M9 13l2 2 4-4"/>',
  collapse: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M15 4v16"/>',
  overflow: '<circle cx="5" cy="12" r="1.8" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.8" fill="currentColor" stroke="none"/>',
  resize: '<rect x="3" y="11" width="10" height="10" rx="1"/><path d="M13 11l8-8"/><path d="M15 3h6v6"/>',
  barcode: '<path d="M4 5v14M20 5v14"/><path d="M8 5v14M11.5 5v14M16 5v14" stroke-width="1.4"/><path d="M13.75 5v14" stroke-width="2.6"/>',
};

/** Icons whose glyph is not square in the source viewBox — `letterO` is the prototype's own 30×24
 *  (its ellipse+strokes need the extra horizontal room letterI's 24×24 box doesn't), everything else
 *  stays the original 24×24. `icon()`/`iconEl()` derive the rendered WIDTH from this so the aspect
 *  ratio is never squeezed, rather than hardcoding a one-off "wide" class on a single caller the way
 *  the prototype's own CSS does (`svg.wide{width:20px}`, applied by name in its `S()` helper) — this
 *  generalizes the same idea to any future non-square glyph without a second special case. */
const VIEWBOXES: Partial<Record<IconName, readonly [w: number, h: number]>> = {
  letterO: [30, 24],
  // Три буквы требуют ширины: в квадрате они сжались бы до нечитаемого.
  pdf: [36, 24],
};

/** Raw `<svg>` markup for a given action, for use inside a template-literal string. */
export function icon(name: IconName, sizePx = 14): string {
  const [vbW, vbH] = VIEWBOXES[name] ?? [24, 24];
  const width = Math.round((sizePx * vbW) / vbH);
  return `<svg class="icon icon-${name}" width="${width}" height="${sizePx}" viewBox="0 0 ${vbW} ${vbH}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${PATHS[name]}</svg>`;
}

/** Live `SVGSVGElement` for a given action, for use in `document.createElement`-style builders. */
export function iconEl(name: IconName, sizePx = 14): SVGSVGElement {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = icon(name, sizePx);
  return wrapper.firstElementChild as SVGSVGElement;
}
